/**
 * Sign in.
 *
 * The dev quick-login list is wrapped in `import.meta.env.DEV`, which Vite
 * replaces with a literal `false` in a production build, so the whole block is
 * dropped by dead-code elimination and the seeded accounts never reach a
 * shipped bundle.
 */
import { useState, type FormEvent } from 'react';
import { LogIn, Zap } from 'lucide-react';
import { api, ApiError } from '../lib/api.ts';
import { useSession } from '../lib/session.tsx';
import { Field, Spinner } from '../components/Bits.tsx';
import { LogoAnimation } from '../components/LogoAnimation.tsx';
import { OutcomeModal } from '../components/Overlay.tsx';

/** Seeded people from packages/db/src/seed.ts, one of each role worth testing. */
const DEV_ACCOUNTS: Array<{ email: string; name: string; role: string }> = [
  { email: 'prlom@icloud.com', name: 'Paul Lommerse', role: 'Owner' },
  { email: 'bec@coffeeemporium.local', name: 'Bec', role: 'Manager' },
  { email: 'ava@coffeeemporium.local', name: 'Ava', role: 'Staff' },
  { email: 'kez@coffeeemporium.local', name: 'Kez', role: 'Staff' },
];

export default function LoginPage() {
  const { setSession } = useSession();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<{ email?: string; password?: string }>({});
  const [failure, setFailure] = useState<string | null>(null);

  async function attempt(run: () => Promise<Awaited<ReturnType<typeof api.login>>>, tag: string) {
    setBusy(tag);
    setFieldError({});
    try {
      const me = await run();
      setSession(me);
    } catch (err) {
      const apiErr = err instanceof ApiError ? err : null;
      if (apiErr?.code === 'bad_credentials') {
        // Both fields are marked: the server cannot say which one was wrong, and
        // guessing would be a hint to anyone probing the form.
        setFieldError({ email: ' ', password: 'That email and password do not match an account.' });
      }
      setFailure(apiErr?.message ?? 'Could not sign you in. Try again in a moment.');
    } finally {
      setBusy(null);
    }
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    const errors: { email?: string; password?: string } = {};
    if (!email.trim()) errors.email = 'Enter the email address you were invited with.';
    if (!password) errors.password = 'Enter your password.';
    if (Object.keys(errors).length > 0) {
      setFieldError(errors);
      setFailure('Some details are missing. The fields that need attention are marked.');
      return;
    }
    void attempt(() => api.login(email.trim(), password), 'form');
  }

  return (
    <div className="flex min-h-screen flex-col">
      <main className="flex flex-1 items-center justify-center px-5 py-10">
        <div className="w-full" style={{ maxWidth: 400 }}>
          <div className="mb-7 flex flex-col items-center text-center">
            {/* The animation carries the wordmark, so repeating it as a heading
                underneath would say the brand name twice. */}
            <LogoAnimation width={300} />
            <h1 className="sr-only">Sneaky Panda Staff App</h1>
            <p className="mt-3 text-[14px] text-muted">Rosters, clocking and pay, without the fuss.</p>
          </div>

          <form onSubmit={onSubmit} className="card card-lift p-6" noValidate>
            <Field label="Email" error={fieldError.email?.trim() ? fieldError.email : null} id="login-email">
              <input
                id="login-email"
                className="field"
                type="email"
                autoComplete="username"
                inputMode="email"
                value={email}
                aria-invalid={fieldError.email ? 'true' : undefined}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
              />
            </Field>
            <Field label="Password" error={fieldError.password} id="login-password">
              <input
                id="login-password"
                className="field"
                type="password"
                autoComplete="current-password"
                value={password}
                aria-invalid={fieldError.password ? 'true' : undefined}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Your password"
              />
            </Field>
            <button type="submit" className="btn btn-primary btn-lg w-full" disabled={busy !== null}>
              {busy === 'form' ? <Spinner size={16} label="Signing in" /> : <LogIn size={16} aria-hidden="true" />}
              Sign in
            </button>
            <p className="mt-4 text-center text-[13px]">
              <a href="/forgot" className="font-semibold text-accent underline-offset-2 hover:underline">
                Forgot your password?
              </a>
            </p>
            <p className="mt-3 text-center text-[12.5px] leading-relaxed text-faint">
              No password yet? Open the invite link your manager sent you and set one there. We never email
              passwords, only links to choose your own.
            </p>
          </form>

          {import.meta.env.DEV ? (
            <section className="card mt-5 p-4">
              <h2 className="flex items-center gap-2 text-[12px] font-bold uppercase tracking-wide text-faint">
                <Zap size={13} aria-hidden="true" />
                Dev quick login
              </h2>
              <p className="mt-1 text-[12px] leading-snug text-faint">
                Development builds only. Signs in as a seeded account with no password.
              </p>
              <div className="mt-3 grid gap-2">
                {DEV_ACCOUNTS.map((a) => (
                  <button
                    key={a.email}
                    type="button"
                    className="btn justify-between"
                    disabled={busy !== null}
                    onClick={() => void attempt(() => api.devLogin(a.email), a.email)}
                    data-tip={`Sign in as ${a.name}|${a.email}`}
                  >
                    <span className="truncate">{a.name}</span>
                    <span className="chip chip-neutral">{a.role}</span>
                  </button>
                ))}
              </div>
            </section>
          ) : null}
        </div>
      </main>

      <OutcomeModal
        open={failure !== null}
        onClose={() => setFailure(null)}
        ok={false}
        title="Could not sign in"
        message={failure ?? ''}
      />
    </div>
  );
}
